import { assertValue } from '@shared/helpers'
import {
  calculateLoanTimeInMonths,
  convertMonthsToYearsAndMonths,
  getLoanAmount,
  PaymentPlanInfoData,
  SecLoanApplicationType,
} from '@shared/sec-loans'
import { OptionalAndNullable } from '@shared/typeHelpers'
import { convertUndefinedToNull } from '@ui-common/utils/form'
import { isNumber, numberToString, toNumber } from '@ui-common/utils/helpers'
import { COMMON_PLACEHOLDER } from '@ui-components/global/constants'
import { IntlShape } from 'react-intl'

import {
  MAX_CONSTANT_DURATION_LOAN_TIME_IN_YEARS,
  MAX_CONSTANT_PAYMENT_LOAN_TIME_IN_YEARS,
  MAX_PURCHASE_LOAN_TIME_IN_YEARS,
} from '../../../../constants'
import { PaymentPlanInfoStepData, StepDataValidator } from '../../../types'
import { isFormDataValid } from '../../../wizardData'
import { createFormValidator } from './formValidator'
import { PaymentPlanInfoFormData } from './PaymentPlanInfoStep'

export const convertToFormValues = (
  paymentPlanInfoStepData: PaymentPlanInfoStepData | undefined,
): OptionalAndNullable<PaymentPlanInfoFormData> => {
  const approximatedRepayment = paymentPlanInfoStepData?.approximatedRepayment
  return convertUndefinedToNull({
    methodOfRepayment: paymentPlanInfoStepData?.methodOfRepayment,
    repaymentFreeMonthsAmount: numberToString(paymentPlanInfoStepData?.repaymentFreeMonthsAmount),
    loanTimeInYears: numberToString(paymentPlanInfoStepData?.loanTimeInYears),
    approximatedRepayment: numberToString(approximatedRepayment),
  })
}

export const convertToPaymentPlanSubmitData = (formData: PaymentPlanInfoFormData): PaymentPlanInfoStepData => {
  const {
    methodOfRepayment: submitMethodOfRepayment,
    repaymentFreeMonthsAmount: submitRepaymentFreeMonthsAmount,
    loanTimeInYears: submitLoanTimeInYears,
    approximatedRepayment: submitApproximatedRepayment,
  } = formData
  return {
    methodOfRepayment: submitMethodOfRepayment,
    repaymentFreeMonthsAmount: assertValue(
      toNumber(submitRepaymentFreeMonthsAmount),
      'submitRepaymentFreeMonthsAmount',
    ),
    loanTimeInYears: toNumber(submitLoanTimeInYears),
    approximatedRepayment: toNumber(submitApproximatedRepayment),
  }
}

export const getAndValidatePaymentPlanInfoTypeData: StepDataValidator<PaymentPlanInfoData | undefined> = ({
  validationData,
  applicationMetadata,
}) => {
  const metadata = assertValue(applicationMetadata, 'applicationMetadata')
  const loanAmount = getLoanAmount(
    validationData.targetInfo,
    validationData.loanInfo,
    metadata.applicationType,
    metadata.applicationPurpose,
  )

  return isFormDataValid(
    convertToFormValues(validationData.loanInfo?.paymentPlanInfoData),
    (formatMessage: IntlShape['formatMessage']) => createFormValidator(formatMessage, metadata, loanAmount),
  )
    ? validationData.loanInfo?.paymentPlanInfoData
    : undefined
}

export const getFormattedLoanTimeInYearsAndMonths = (
  formatMessage: IntlShape['formatMessage'],
  totalLoanAmount: number,
  approximatedRepayment: string | undefined,
  loanInterestRate: number,
): string => {
  const approximatedRepaymentNumber = toNumber(approximatedRepayment)

  if (isNumber(approximatedRepaymentNumber)) {
    const calculatedMonths = calculateLoanTimeInMonths(totalLoanAmount, loanInterestRate, approximatedRepaymentNumber)
    const { years, months } = convertMonthsToYearsAndMonths(calculatedMonths)
    return isNumber(years) && isNumber(months)
      ? formatMessage({ id: 'sec-loans-payment-plan-calculated-estimated-loan-time' }, { years, months })
      : COMMON_PLACEHOLDER
  }

  return COMMON_PLACEHOLDER
}

export const getMaxConstantDurationLoanTimeYears = (applicationType: SecLoanApplicationType): number =>
  applicationType === SecLoanApplicationType.PurchaseLoan
    ? MAX_PURCHASE_LOAN_TIME_IN_YEARS
    : MAX_CONSTANT_DURATION_LOAN_TIME_IN_YEARS

export const getMaxConstantPaymentLoanTimeYears = (applicationType: SecLoanApplicationType): number =>
  applicationType === SecLoanApplicationType.PurchaseLoan
    ? MAX_PURCHASE_LOAN_TIME_IN_YEARS
    : MAX_CONSTANT_PAYMENT_LOAN_TIME_IN_YEARS
